'use client';

import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import { DataCellSkeleton } from '@/components/data-cell';
import { PROPOSAL_DETAIL_MAIN_COLUMN_CLASS, PROPOSAL_DETAIL_RIGHT_RAIL_CLASS, PROPOSAL_DETAIL_ROW_CLASS } from '@/components/launchpad/proposal-detail-layout-classes';
import { Skeleton } from '@/components/ui/app-skeleton';
import { Panel, PanelContent } from '@/components/ui/panel';

/**
 * Loading state for the proposal detail page. Mirrors the structure of the
 * loaded layout (header + hero, summary data-cell row, creator card +
 * data cells, backers region, desktop sidebar) to reduce layout shift on hydrate.
 */
export function ProposalDetailSkeleton() {
  const $ = _c(14);
  const t = useTranslations("launchpad.detail");
  let t0;
  if ($[0] !== t) {
    t0 = t("skeletonAriaLabel");
    $[0] = t;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Skeleton className="h-28 w-full rounded-card" />;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  let t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="flex flex-wrap items-center gap-2"><Skeleton className="h-5 w-20" /><Skeleton className="h-5 w-24" /><Skeleton className="h-5 w-16" /></div>;
    t3 = <Skeleton className="h-7 w-3/4" />;
    t4 = <Skeleton className="h-5 w-32" />;
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
  } else {
    t2 = $[3];
    t3 = $[4];
    t4 = $[5];
  }
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Panel elevation="elevated" className="flex min-w-0 flex-col gap-4 p-4" data-testid="proposal-detail-header-panel">{t1}{t2}{t3}{t4}<div className="flex flex-col gap-3"><Skeleton className="h-2 w-full rounded-full" /><Skeleton className="h-4 w-full" /></div></Panel>;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className="flex gap-3 sm:gap-4"><DataCellSkeleton className="flex-1" /><DataCellSkeleton className="flex-1" /></div>;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Skeleton className="size-10 shrink-0 rounded-full" />;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <header className="flex items-center justify-between gap-4"><div className="flex min-w-0 items-center gap-3">{t7}<div className="flex min-w-0 flex-col gap-0.5"><Skeleton className="h-4 w-24 max-w-full" /><Skeleton className="h-5 w-36 max-w-full" /></div></div><Skeleton className="h-4 w-14 shrink-0" /></header>;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <div className={PROPOSAL_DETAIL_MAIN_COLUMN_CLASS}>{t5}{t6}<Panel surface="solid" density="compact"><PanelContent className="flex flex-col gap-4">{t8}<div className="flex gap-3 sm:gap-4"><DataCellSkeleton cornerRadius="panelInset" className="flex-1" /><DataCellSkeleton cornerRadius="panelInset" className="flex-1" /></div></PanelContent></Panel><Skeleton className="h-72 w-full rounded-card" /></div>;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  let t10;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <div className={PROPOSAL_DETAIL_ROW_CLASS}>{t9}<div className={PROPOSAL_DETAIL_RIGHT_RAIL_CLASS}><Skeleton className="h-72 w-full rounded-card" /><Skeleton className="h-48 w-full rounded-card" /></div></div>;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  if ($[12] !== t0) {
    t11 = <output className="block" aria-label={t0} aria-busy="true">{t10}</output>;
    $[12] = t0;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  return t11;
}